import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

const PreguntaRowMobile = React.memo(({ p, isSelected, onToggle }) => {
  return (
    <Card sx={{ mb: 1, backgroundColor: isSelected ? '#e3f2fd' : 'inherit' }}>
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
          <Switch
            checked={isSelected}
            onChange={onToggle}
            color="secondary"
            size="small"
          />
          <Box flex={1}>
            <Typography variant="body2" fontWeight="medium">
              {p.titulo}
            </Typography>
            {p.descripcion && (
              <Typography variant="caption" color="textSecondary">
                {p.descripcion}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
          <Chip
            label={p.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
            size="small"
            variant="outlined"
            color={p.tipo_persona === 'natural' ? 'primary' : 'secondary'}
          />
          {p.tipo !== 'certificado' && (
            <>
              <Chip
                label={p.seccion || 'Sin sección'}
                size="small"
                variant="filled"
                color={p.seccion === '5_5' ? 'primary' : 'default'}
              />
              <Typography variant="body2" fontWeight="bold" color={p.seccion === '5_5' ? 'primary' : 'textPrimary'}>
                {p.puntaje_maximo || 0} pts
              </Typography>
            </>
          )}
          {p.tipo === 'certificado' && (
            <Typography variant="caption" color="textSecondary" fontStyle="italic">
              N/A
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

const FormularioBasicFields = React.memo(({ formData, setFormData, isMobile }) => {
  const [localTitulo, setLocalTitulo] = useState(formData.titulo);
  const [localDescripcion, setLocalDescripcion] = useState(formData.descripcion);

  const handleTituloBlur = () => {
    setFormData(prev => ({ ...prev, titulo: localTitulo }));
  };

  const handleDescripcionBlur = () => {
    setFormData(prev => ({ ...prev, descripcion: localDescripcion }));
  };

  return (
    <>
      <TextField
        label="Título"
        value={localTitulo}
        onChange={(e) => setLocalTitulo(e.target.value)}
        onBlur={handleTituloBlur}
        fullWidth
        required
        size={isMobile ? "small" : "medium"}
        inputProps={{ maxLength: 80 }}
      />
      <TextField
        label="Descripción"
        value={localDescripcion}
        onChange={(e) => setLocalDescripcion(e.target.value)}
        onBlur={handleDescripcionBlur}
        multiline
        rows={isMobile ? 2 : 3}
        fullWidth
        size={isMobile ? "small" : "medium"}
        inputProps={{ maxLength: 200 }}
      />
    </>
  );
});

const FormularioDialog = ({
  openDialog,
  setOpenDialog,
  isEditing,
  formData,
  setFormData,
  isMobile,
  preguntas,
  selectedPreguntas,
  setSelectedPreguntas,
  handleSave,
  saving
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [filtroTipoPersona, setFiltroTipoPersona] = useState('TODAS');
  const [filtroSeccion, setFiltroSeccion] = useState('TODAS');
  const [filtroTexto, setFiltroTexto] = useState('');

  const preguntasDocumentos = useMemo(() => preguntas.filter((p) => p.tipo === 'documento'), [preguntas]);
  const preguntasCertificados = useMemo(() => preguntas.filter((p) => p.tipo === 'certificado'), [preguntas]);

  const preguntasFiltradas = useMemo(() => {
    const preguntasList = tabValue === 0 ? preguntasDocumentos : preguntasCertificados;
    return preguntasList.filter((p) => {
      const isActive = p.activo === 1 || p.activo === '1' || p.activo === true || p.activo === 'true';
      if (!isActive) return false;
      const coincideTipo = filtroTipoPersona === 'TODAS' || p.tipo_persona === filtroTipoPersona;
      const coincideSeccion = filtroSeccion === 'TODAS' || p.seccion === filtroSeccion;
      const coincideTexto = filtroTexto === '' || 
        p.titulo.toLowerCase().includes(filtroTexto.toLowerCase()) || 
        (p.descripcion && p.descripcion.toLowerCase().includes(filtroTexto.toLowerCase()));
      return coincideTipo && coincideSeccion && coincideTexto;
    });
  }, [tabValue, preguntasDocumentos, preguntasCertificados, filtroTipoPersona, filtroSeccion, filtroTexto]);

  const calcularSumaPuntajesPorTipo = useCallback((seccion, tipoPersona) => {
    return selectedPreguntas.reduce((sum, preguntaId) => {
      const pregunta = preguntas.find(p => p.id_pregunta === preguntaId);
      if (pregunta && pregunta.tipo === 'documento' && pregunta.seccion === seccion && pregunta.tipo_persona === tipoPersona) {
        return sum + (parseFloat(pregunta.puntaje_maximo) || 0);
      }
      return sum;
    }, 0);
  }, [selectedPreguntas, preguntas]);

  const sumaSeccion5_5Natural = useMemo(() => calcularSumaPuntajesPorTipo('5_5', 'natural'), [calcularSumaPuntajesPorTipo]);
  const sumaSeccion5_5Juridica = useMemo(() => calcularSumaPuntajesPorTipo('5_5', 'juridica'), [calcularSumaPuntajesPorTipo]);

  const handleSeleccionarTodasPreguntas = useCallback(() => {
    const ids = preguntasFiltradas.map((p) => p.id_pregunta);
    const todosSeleccionados = ids.every((id) => selectedPreguntas.includes(id));
    if (todosSeleccionados) {
      setSelectedPreguntas((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedPreguntas((prev) => Array.from(new Set([...prev, ...ids])));
    }
  }, [preguntasFiltradas, selectedPreguntas]);

  const handleLimpiarFiltros = useCallback(() => {
    setFiltroTexto('');
    setFiltroTipoPersona('TODAS');
    setFiltroSeccion('TODAS');
  }, []);

  const handleInputChange = useCallback((field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleTogglePregunta = useCallback((idPregunta) => {
    setSelectedPreguntas(prev =>
      prev.includes(idPregunta)
        ? prev.filter(id => id !== idPregunta)
        : [...prev, idPregunta]
    );
  }, []);

  const hayFiltrosActivos = filtroTexto !== '' || filtroTipoPersona !== 'TODAS' || filtroSeccion !== 'TODAS';

  return (
    <Dialog
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      sx={{ '& .MuiDialog-paper': { width: '90%', maxWidth: '1200px', m: 1 } }}
    >
      <DialogTitle>{isEditing ? 'Editar Formulario' : 'Generar Formulario'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>

          <FormularioBasicFields
            formData={formData}
            setFormData={setFormData}
            isMobile={isMobile}
          />

          <Box display="flex" gap={2} flexDirection={isMobile ? "column" : "row"}>
            <TextField
              label="Fecha Inicio"
              type="date"
              value={formData.fecha_inicio}
              onChange={handleInputChange('fecha_inicio')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Fecha Fin"
              type="date"
              value={formData.fecha_fin}
              onChange={handleInputChange('fecha_fin')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
          </Box>

          <Typography variant="h6" gutterBottom>
            Parámetros del Formulario
          </Typography>

          <Box display="flex" gap={3} mb={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }} flexDirection={isMobile ? "column" : "row"}>
            <Box flex={1}>
              <Typography variant="subtitle2" color="textSecondary">
                Sección 5_5 - Persona Natural
              </Typography>
              <Typography
                variant="h6"
                color={sumaSeccion5_5Natural === 100 ? 'success' : sumaSeccion5_5Natural > 100 ? 'error' : 'warning'}
              >
                {sumaSeccion5_5Natural} / 100 puntos
              </Typography>
              {sumaSeccion5_5Natural !== 100 && (
                <Typography variant="caption" color={sumaSeccion5_5Natural > 100 ? 'error' : 'warning'}>
                  {sumaSeccion5_5Natural > 100 ? `Excede por ${sumaSeccion5_5Natural - 100} puntos` : `Faltan ${100 - sumaSeccion5_5Natural} puntos`}
                </Typography>
              )}
            </Box>

            <Box flex={1}>
              <Typography variant="subtitle2" color="textSecondary">
                Sección 5_5 - Persona Jurídica
              </Typography>
              <Typography
                variant="h6"
                color={sumaSeccion5_5Juridica === 100 ? 'success' : sumaSeccion5_5Juridica > 100 ? 'error' : 'warning'}
              >
                {sumaSeccion5_5Juridica} / 100 puntos
              </Typography>
              {sumaSeccion5_5Juridica !== 100 && (
                <Typography variant="caption" color={sumaSeccion5_5Juridica > 100 ? 'error' : 'warning'}>
                  {sumaSeccion5_5Juridica > 100 ? `Excede por ${sumaSeccion5_5Juridica - 100} puntos` : `Faltan ${100 - sumaSeccion5_5Juridica} puntos`}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexDirection={isMobile ? "column" : "row"} flexWrap="wrap">
            <TextField
              label="Buscar por título o descripción"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              size="small"
              sx={{ minWidth: isMobile ? '100%' : 300, flex: 1 }}
            />
            <Box display="flex" gap={2} alignItems="center" flexDirection={isMobile ? "column" : "row"} width={isMobile ? '100%' : 'auto'}>
              <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
                <InputLabel>Tipo Persona</InputLabel>
                <Select
                  value={filtroTipoPersona}
                  onChange={(e) => setFiltroTipoPersona(e.target.value)}
                  label="Tipo Persona"
                >
                  <MenuItem value="TODAS">Todas</MenuItem>
                  <MenuItem value="natural">Natural</MenuItem>
                  <MenuItem value="juridica">Jurídica</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
                <InputLabel>Sección</InputLabel>
                <Select
                  value={filtroSeccion}
                  onChange={(e) => setFiltroSeccion(e.target.value)}
                  label="Sección"
                >
                  <MenuItem value="TODAS">Todas</MenuItem>
                  <MenuItem value="5_4">5_4</MenuItem>
                  <MenuItem value="5_5">5_5</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Limpiar filtros">
                <IconButton 
                  onClick={handleLimpiarFiltros} 
                  size="small"
                  sx={{
                    backgroundColor: hayFiltrosActivos ? '#003366' : 'transparent',
                    color: hayFiltrosActivos ? 'white' : '#003366',
                    '&:hover': {
                      backgroundColor: hayFiltrosActivos ? '#002244' : 'rgba(0,51,102,0.04)'
                    },
                    border: '1px solid',
                    borderColor: hayFiltrosActivos ? '#003366' : '#003366',
                    borderRadius: '4px',
                    width: 36,
                    height: 36
                  }}
                >
                  <i className="bi bi-funnel"></i>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="Documentos" />
              <Tab label="Políticas" />
            </Tabs>
            <Button variant="outlined" size="small" onClick={handleSeleccionarTodasPreguntas}>
              Seleccionar todas
            </Button>
          </Box>

          {isMobile ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {preguntasFiltradas.map((p) => (
                <PreguntaRowMobile
                  key={p.id_pregunta}
                  p={p}
                  isSelected={selectedPreguntas.includes(p.id_pregunta)}
                  onToggle={() => handleTogglePregunta(p.id_pregunta)}
                />
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell><strong>Título</strong></TableCell>
                    <TableCell><strong>Descripción</strong></TableCell>
                    <TableCell><strong>Tipo Persona</strong></TableCell>
                    {tabValue === 0 && (
                      <>
                        <TableCell><strong>Sección</strong></TableCell>
                        <TableCell align="right"><strong>Puntaje</strong></TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preguntasFiltradas.map((p) => {
                    const isSelected = selectedPreguntas.includes(p.id_pregunta);
                    return (
                      <TableRow
                        key={p.id_pregunta}
                        sx={{
                          backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
                          '&:hover': { backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5' }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Switch
                            checked={isSelected}
                            onChange={() => handleTogglePregunta(p.id_pregunta)}
                            color="secondary"
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {p.titulo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {p.descripcion || 'Sin descripción'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={p.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
                            size="small"
                            variant="outlined"
                            color={p.tipo_persona === 'natural' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        {tabValue === 0 && (
                          <>
                            <TableCell>
                              {p.tipo === 'certificado' ? (
                                <Chip label="N/A" size="small" variant="outlined" color="default" />
                              ) : (
                                <Chip
                                  label={p.seccion || 'Sin sección'}
                                  size="small"
                                  variant="filled"
                                  color={p.seccion === '5_5' ? 'primary' : 'default'}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {p.tipo === 'certificado' ? (
                                <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                  N/A
                                </Typography>
                              ) : (
                                <Typography variant="body2" fontWeight="bold" color={p.seccion === '5_5' ? 'primary' : 'textPrimary'}>
                                  {p.puntaje_maximo || 0} pts
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.titulo || selectedPreguntas.length === 0 || saving || sumaSeccion5_5Natural !== 100 || sumaSeccion5_5Juridica !== 100}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Guardando…' : isEditing ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormularioDialog;